/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Role } from '../../shared/entities/role';
import { Action, ActionCreators } from './actions';

export interface State {
  readonly roles: Role[] | undefined;
  readonly isRequestingRoles: boolean;
  readonly isRolesRequestFinished: boolean;
  readonly rolesRequestError?: string;

  readonly currentRole: Role | undefined;
  readonly isRequestingCurrentRole: boolean;
  readonly isCurrentRoleRequestFinished: boolean;
  readonly currentRoleRequestError?: string;

  readonly isProvisioningRole: boolean;
  readonly isRoleProvisioningFinished: boolean;
  readonly roleProvisioningError?: string;

  readonly isDeletingRole: boolean;
  readonly isDeletingRoleFinished: boolean;
  readonly deleteRoleError?: string;

  readonly isUpdatingRole: boolean;
  readonly isUpdatingRoleFinished: boolean;
  readonly updateRoleError?: string;
}

export const initialRoleProvisioningState: State = {
  roles: undefined,
  isRequestingRoles: false,
  isRolesRequestFinished: false,
  rolesRequestError: undefined,

  currentRole: undefined,
  isRequestingCurrentRole: false,
  isCurrentRoleRequestFinished: false,
  currentRoleRequestError: undefined,

  isProvisioningRole: false,
  isRoleProvisioningFinished: false,
  roleProvisioningError: undefined,

  isDeletingRole: false,
  isDeletingRoleFinished: false,
  deleteRoleError: undefined,

  isUpdatingRole: false,
  isUpdatingRoleFinished: false,
  updateRoleError: undefined,
};

export default function reducer(state: State = initialRoleProvisioningState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    // For requesting list of roles
    case ActionCreators.RequestRoles.type:
      partialState = {
        roles: undefined,
        isRequestingRoles: true,
        isRolesRequestFinished: false,
        rolesRequestError: undefined,
      };
      break;

    case ActionCreators.ReceivedRolesSuccess.type: {
      // Sort roles by name
      const sortedRoles = action.payload.response.sort((a: Role, b: Role) => a.name?.localeCompare(b.name));

      partialState = {
        roles: sortedRoles,
        isRequestingRoles: false,
        isRolesRequestFinished: true,
        rolesRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedRolesError.type:
      partialState = {
        isRequestingRoles: false,
        isRolesRequestFinished: true,
        rolesRequestError: action.payload.message,
      };
      break;

    case ActionCreators.ClearReceivedRolesError.type:
      partialState = {
        rolesRequestError: undefined,
      };
      break;

    // For requesting a specific role
    case ActionCreators.RequestCurrentRole.type:
      partialState = {
        currentRole: undefined,
        isRequestingCurrentRole: true,
        isCurrentRoleRequestFinished: false,
        currentRoleRequestError: undefined,
      };
      break;

    case ActionCreators.ReceivedCurrentRoleSuccess.type: {
      partialState = {
        currentRole: action.payload.response,
        isRequestingCurrentRole: false,
        isCurrentRoleRequestFinished: true,
        currentRoleRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedCurrentRoleError.type:
      partialState = {
        isRequestingCurrentRole: false,
        isCurrentRoleRequestFinished: true,
        currentRoleRequestError: action.payload.message,
      };
      break;

    case ActionCreators.ClearReceivedCurrentRoleError.type:
      partialState = {
        currentRoleRequestError: undefined,
      };
      break;

    case ActionCreators.ClearCurrentRole.type:
      partialState = {
        currentRole: undefined,
        isRequestingCurrentRole: false,
        isCurrentRoleRequestFinished: false,
        currentRoleRequestError: undefined,
      };
      break;

    // For creating a role
    case ActionCreators.ProvisionRole.type:
      partialState = {
        isProvisioningRole: true,
        isRoleProvisioningFinished: false,
        roleProvisioningError: undefined,
      };
      break;

    case ActionCreators.RoleProvisioningSuccess.type:
      partialState = {
        isProvisioningRole: false,
        isRoleProvisioningFinished: true,
        roleProvisioningError: undefined,
        currentRole: action.payload,
      };
      break;

    case ActionCreators.RoleProvisioningError.type:
      partialState = {
        isProvisioningRole: false,
        isRoleProvisioningFinished: true,
        roleProvisioningError: action.payload.message,
      };
      break;

    case ActionCreators.ClearRoleProvisioningError.type:
      partialState = {
        roleProvisioningError: undefined,
      };
      break;

    // For deleting a role
    case ActionCreators.DeleteRole.type:
      partialState = {
        isDeletingRole: true,
        isDeletingRoleFinished: false,
        deleteRoleError: undefined,
      };
      break;

    case ActionCreators.DeleteRoleSuccess.type:
      partialState = {
        isDeletingRole: false,
        isDeletingRoleFinished: true,
        deleteRoleError: undefined,
      };
      break;

    case ActionCreators.DeleteRoleError.type:
      partialState = {
        isDeletingRole: false,
        isDeletingRoleFinished: true,
        deleteRoleError: action.payload.message,
      };
      break;

    case ActionCreators.ClearDeleteRoleError.type:
      partialState = {
        deleteRoleError: undefined,
      };
      break;

    // For updating a role
    case ActionCreators.UpdateRole.type:
      partialState = {
        isUpdatingRole: true,
        isUpdatingRoleFinished: false,
        updateRoleError: undefined,
      };
      break;

    case ActionCreators.UpdateRoleSuccess.type:
      partialState = {
        isUpdatingRole: false,
        isUpdatingRoleFinished: true,
        updateRoleError: undefined,
        currentRole: action.payload,
      };
      break;

    case ActionCreators.UpdateRoleError.type:
      partialState = {
        isUpdatingRole: false,
        isUpdatingRoleFinished: true,
        updateRoleError: action.payload.message,
      };
      break;

    case ActionCreators.ClearUpdateRoleError.type:
      partialState = {
        updateRoleError: undefined,
      };
      break;

    default:
      return state;
  }
  return { ...state, ...partialState };
}
